import HappyBoxImage from '@assets/icons/happy-box.svg';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import StaticConfetti from '@assets/icons/confetti-static.svg';
import validator from 'validator';
import {
  AddressInput,
  getAddressInputFromPlaceResult,
} from '@/components/addresses/address-input';
import { AddressInputType } from '@domain/addresses/types/types';
import { AUTH_MODAL_ROUTES, CONTAINER_MODAL_ROUTES } from '@/navigators/routes';
import {
  AuthModalStackParams,
  ContainerModalStackParams,
} from '@/navigators/types';
import { Button } from '@/components/button';
import { CompositeNavigationProp } from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { EmailInput } from '@/components/email-input';
import { KeyboardAwareScrollView } from '@/components/with-keyboard-aware';
import { ModalContainer } from '@/components/modal-container';
import { PhoneNumberInput } from '@/components/phone-number-input';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { StackNavigationProp } from '@react-navigation/stack';
import { Text, View } from 'react-native';
import { TextInput } from '@/components/text-input';
import { useAuthStackContext } from '@/contexts/auth-stack-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSubmitProspectiveCustomer } from '@/hooks/use-submit-prospective-customer';
import { useToasts } from '@/contexts/toast-context';

export type Navigation = CompositeNavigationProp<
  StackNavigationProp<AuthModalStackParams, AUTH_MODAL_ROUTES.BecomeCustomer>,
  StackNavigationProp<
    ContainerModalStackParams,
    CONTAINER_MODAL_ROUTES.PlacesSearch
  >
>;

interface Props {
  navigation: Navigation;
}

export const BecomeCustomerScreen: React.FC<Props> = ({ navigation }) => {
  const { bottom } = useSafeAreaInsets();
  const { isSubmitting, submitProspectiveCustomerCallback } =
    useSubmitProspectiveCustomer();
  const {
    becomeCustomerLabels,
    prospectiveCustomerSubmitted,
    setProspectiveCustomerSubmitted,
  } = useAuthStackContext();
  const { showErrorToast } = useToasts();
  const [styles, theme] = useStyles();

  const [business, setBusiness] = useState<string>('');
  const [businessAddress, setBusinessAddress] =
    useState<AddressInputType | null>(null);
  const [businessAddress2, setBusinessAddress2] = useState('');
  const [contactName, setContactName] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');

  if (!becomeCustomerLabels) {
    return null;
  }

  const isBecomeCustomerButtonDisabled =
    !business || !businessAddress || !contactName || !email || !phoneNumber;

  const form = (
    <KeyboardAwareScrollView
      contentContainerStyle={[
        styles.formContentContainer,
        { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
      ]}
      extraSpacing={40}
      keyboardDismissMode="on-drag"
    >
      <View style={styles.inputFieldsContainer}>
        <TextInput
          containerStyle={styles.inputFieldContainer}
          focusedBorderColor={theme.colors.accent}
          label={business ? `${becomeCustomerLabels.name}*` : null}
          onChangeText={setBusiness}
          placeholder={`${becomeCustomerLabels.name}*`}
          testID="business-input"
          value={business}
        />
        <AddressInput
          address2={businessAddress2}
          address2Props={{
            containerStyle: styles.inputFieldContainer,
            testID: 'business-address-2-input',
          }}
          addressProps={{
            containerStyle: styles.inputFieldContainer,
            testID: 'business-address-input',
          }}
          label={`${becomeCustomerLabels.address}*`}
          onAddress2Change={setBusinessAddress2}
          onOpenSearch={() => {
            navigation.navigate(CONTAINER_MODAL_ROUTES.PlacesSearch, {
              initialValue: businessAddress?.address || '',
              onSelect: (
                _,
                placeResult: google.maps.places.PlaceResult | null,
              ) => {
                if (placeResult) {
                  setBusinessAddress(
                    getAddressInputFromPlaceResult(
                      placeResult,
                      businessAddress2,
                    ),
                  );
                }
              },
              placeholder: becomeCustomerLabels.address,
              types: ['address'],
            });
          }}
          value={businessAddress}
        />
        <TextInput
          containerStyle={styles.inputFieldContainer}
          focusedBorderColor={theme.colors.accent}
          label={contactName ? `${i18n.t('contact_name')}*` : null}
          onChangeText={setContactName}
          placeholder={`${i18n.t('contact_name')}*`}
          testID="contact-name-input"
          value={contactName}
        />
        <PhoneNumberInput
          containerStyle={styles.inputFieldContainer}
          focusedBorderColor={theme.colors.accent}
          label={phoneNumber ? `${i18n.t('phone_number')}*` : null}
          onChangeText={setPhoneNumber}
          placeholder={`${i18n.t('phone_number')}*`}
          testID="phone-number-input"
        />
        <EmailInput
          containerStyle={styles.inputFieldContainer}
          focusedBorderColor={theme.colors.accent}
          label={email ? `${i18n.t('email')}*` : null}
          onChangeText={setEmail}
          placeholder={`${i18n.t('email')}*`}
          testID="email-input"
          value={email}
        />
      </View>
      <Button
        disabled={isBecomeCustomerButtonDisabled}
        loading={isSubmitting}
        onPress={async () => {
          const trimmedEmail = email.trim();
          if (!validator.isEmail(trimmedEmail)) {
            showErrorToast(i18n.t('toast.invalid_email'));
            return;
          }

          const restaurantAddress = businessAddress2
            ? `${businessAddress2}, ${businessAddress?.address!}`
            : businessAddress?.address!;

          try {
            await submitProspectiveCustomerCallback({
              contactEmail: trimmedEmail,
              contactName,
              contactPhoneNumber: phoneNumber!,
              restaurantAddress,
              restaurantName: business,
            });
            setProspectiveCustomerSubmitted(true);
          } catch (e) {
            showErrorToast();
          }
        }}
        testID="become-customer-button"
        textStyle={styles.buttonText}
        trackingData={{ name: 'welcome.become_customer_button' }}
      >
        {i18n.t('submit')}
      </Button>
    </KeyboardAwareScrollView>
  );

  const confirmation = (
    <View
      style={[
        styles.confirmationContainer,
        { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
      ]}
    >
      <View style={styles.imageContainer} testID="happy-box-confetti">
        <StaticConfetti
          accentFill={theme.colors.accent}
          fill={theme.colors.primary}
        />
        <HappyBoxImage fill={theme.colors.primary} style={styles.happyBox} />
      </View>
      <Text style={[TYPOGRAPHY.h2, styles.confirmationText]}>
        {i18n.t('application_being_processed')}
      </Text>
      <Text style={[TYPOGRAPHY.p, styles.confirmationText]}>
        {i18n.t('reach_out_soon.application')}
      </Text>
      <Button
        containerStyle={styles.buttonContainer}
        onPress={() => navigation.goBack()}
        testID="exit-become-customer-button"
        trackingData={{ name: 'welcome.become_customer_exit_button' }}
      >
        {i18n.t('exit')}
      </Button>
    </View>
  );

  return (
    <ModalContainer
      navigationHeaderProps={{ title: i18n.t('become_customer') }}
      shouldDismissKeyboard
    >
      {prospectiveCustomerSubmitted ? confirmation : form}
    </ModalContainer>
  );
};

const imageTopMargin = 36;

const useStyles = createStyles((theme) => ({
  buttonContainer: {
    paddingTop: SCREEN_PADDING,
  },
  buttonText: {
    color: theme.colors.authBackground,
  },
  confirmationContainer: {
    gap: 8,
    paddingHorizontal: SCREEN_PADDING,
  },
  confirmationText: {
    textAlign: 'center',
  },
  formContentContainer: {
    flexGrow: 1,
    paddingHorizontal: SCREEN_PADDING,
  },
  happyBox: {
    marginTop: -imageTopMargin,
  },
  imageContainer: {
    alignItems: 'center',
    marginTop: imageTopMargin,
  },
  inputFieldContainer: {
    borderColor: theme.colors.stroke,
    borderRadius: 4,
    borderWidth: 1,
    marginBottom: 16,
  },
  inputFieldsContainer: {
    flex: 1,
  },
}));
